import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { ApiError } from '@/api/http';
import { queryKeys } from '@/api/queryKeys';
import type { TaskEmailLink } from '@/api/types';
import { ACCOUNT_PARAM } from '@/app/accountSearchParam';
import { Icon } from '@/components/Icon';

/**
 * "Open email" on a task made from one (ADR 0075).
 *
 * The link says where the email was. A UID changes when mail moves, so the
 * button asks the server where it is NOW: at the same place, or re-found by
 * its Message-ID in another folder (the server then keeps the new place). A
 * message in no folder any more is said plainly, and so is a search that
 * could not reach every folder — "not found" would be a guess there.
 *
 * The mail route names the mailbox (`?account=`), like a notification's
 * click: folder and message ids mean something only inside one mailbox.
 */
export function mailPathFor(accountId: string, folderId: string, messageId: string): string {
  return (
    `/mail/${encodeURIComponent(folderId)}/${encodeURIComponent(messageId)}` +
    `?${ACCOUNT_PARAM}=${encodeURIComponent(accountId)}`
  );
}

export function OpenEmailButton({ taskId, email }: { taskId: string; email: TaskEmailLink }) {
  const api = useApi();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const open = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      const where = await api.tasks.resolveEmail(email.accountId, taskId);
      if ((where.status === 'found' || where.status === 'moved') && where.messageId && where.folderId) {
        // A moved email: the task's copy of the link is stale now.
        if (where.status === 'moved') {
          void client.invalidateQueries({ queryKey: queryKeys.account(email.accountId).tasksScope() });
        }
        navigate(mailPathFor(email.accountId, where.folderId, where.messageId));
        return;
      }
      setProblem(
        where.status === 'gone'
          ? 'This email isn’t in the mailbox any more — it may have been deleted.'
          : 'Some folders didn’t answer in time, so the email wasn’t found. Try again in a moment.',
      );
    } catch (cause) {
      setProblem(
        cause instanceof ApiError && cause.isOffline
          ? 'You’re offline. Open the email once the connection is back.'
          : cause instanceof ApiError && cause.status >= 400 && cause.status < 500 && cause.message
            ? cause.message
            : 'The email couldn’t be looked up. Try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tk-detail__email">
      <button type="button" className="btn tk-detail__email-open" disabled={busy} onClick={() => void open()}>
        <Icon name="mail-unread" size={16} /> {busy ? 'Finding the email…' : 'Open email'}
      </button>
      {problem !== undefined && (
        <p className="tk-detail__email-problem" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}
